import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DownloadButton from 'components/@home/modals/DocumentModal/DownloadButton';
import Loading from 'components/common/Loading';
import { bool, func, object, shape } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { getDigitalSignatureCardTitle } from 'utils/getDigitalSignatureCardCopy';
import { I18n } from 'utils/i18n';
import { mediaFetchCredentials } from 'utils/mediaFetchCredentials';
import { contentDispositionIsAttachment } from 'utils/ommniosignSignedZipDownload';
import resolveSignedDocumentUrl from 'utils/resolveSignedDocumentUrl';
import channelShape from 'shapes/channel';
import employeeShape from 'shapes/employee';
import messageShape from 'shapes/message';
import Modal from '../../Modal';
import connector from './connector';
import Information from './Information';
import IUnderstoodButton from './IUnderstoodButton';

const getPdf = message =>
  message?.attachments?.[0].httpLink || message?.attachments?.[0].toString();
const getPdfName = message => message.attachments?.[0]?.name;
const getMimeType = message => message.attachments?.[0]?.mimeType;

const ReadModal = ({
  isOpen,
  actions,
  message = null,
  employee = null,
  channel = undefined,
  authEmployee = null,
  isCompanyAdmin,
  isTeamAdmin,
  log = false,
}) => {
  const { isDirect, isAdmin } = channel || {};
  const emp = employee || authEmployee;
  const isMe = employee?._id === authEmployee?._id;
  const [pdf, setPdf] = useState(null);
  const [viewLog, setViewLog] = useState(log);

  // Match backend user id as string (ack.user may be ObjectId or string; compare to user_id or user._id)
  const ackUserId = String(emp?.user_id || emp?.user?._id || '');
  const acknowledge = message?.acknowledges?.find(
    a => a.user != null && String(a.user) === ackUserId,
  );
  const ack = acknowledge || {};

  const needsPdfBackfill = useMemo(() => {
    if (!message?._id || message.digitalSignature || !acknowledge?.confirmedAt) {
      return false;
    }
    if (!ackUserId) return false;
    const hasPdf = (message.attachments || []).some(
      a => a?.mimeType === 'application/pdf' && (a.httpLink || a._id),
    );
    if (!hasPdf) return false;
    return !ack.signedDocUrl || !ack.logUrl;
  }, [
    message?._id,
    message?.digitalSignature,
    message?.attachments,
    acknowledge?.confirmedAt,
    ack.signedDocUrl,
    ack.logUrl,
    ackUserId,
  ]);

  useEffect(() => {
    if (isOpen) setViewLog(log);
  }, [isOpen, log]);

  const effectiveLog = viewLog;

  // oneForAll: prefer finalSignedDocUrl (all signatures). onePerMember must keep
  // the per-user copy — older rows can still have a stale last-signer URL.
  let pdfUrl;
  if (message?.digitalSignature) {
    if (effectiveLog) {
      // Always use individual log for audit trail
      pdfUrl = ack.logUrl;
    } else {
      pdfUrl = resolveSignedDocumentUrl(message, ackUserId, ack.signedDocUrl);
    }
  } else if (effectiveLog) {
    pdfUrl = ack.logUrl || null;
  } else {
    pdfUrl = ack.signedDocUrl || getPdf(message);
  }

  const openAnalyticsDrawer = () => {
    actions.acknowledgmentReadModal.close();
    actions.analyticsDrawer.open(message);
  };

  // Preview uses server URLs only (signed/stamped PDFs and certificates come from the backend).
  // Legacy acknowledges without persisted stamped URLs: show loading while lazy backfill runs.
  // attachment makes the iframe download instead of rendering. inline (and a missing
  // disposition) can use the CDN URL so the PDF toolbar keeps Content-Disposition filename.
  useEffect(() => {
    if (!isOpen || !message?._id) return undefined;

    if (needsPdfBackfill) {
      setPdf(null);
      return undefined;
    }
    if (!pdfUrl) {
      setPdf(null);
      return undefined;
    }

    const isServerSignedPreviewUrl =
      pdfUrl === ack.signedDocUrl || pdfUrl === ack.logUrl || pdfUrl === message?.finalSignedDocUrl;

    if (!message?.digitalSignature && !isServerSignedPreviewUrl) {
      setPdf(pdfUrl);
      return undefined;
    }

    let blobUrl;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(pdfUrl, { credentials: mediaFetchCredentials(pdfUrl) });
        if (!res.ok) throw new Error(String(res.status));
        if (!contentDispositionIsAttachment(res.headers.get('content-disposition'))) {
          await res.body?.cancel?.();
          if (!cancelled) setPdf(pdfUrl);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setPdf(blobUrl);
      } catch {
        if (!cancelled) setPdf(pdfUrl);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [
    isOpen,
    message?._id,
    message?.digitalSignature,
    message?.signingMode,
    message?.finalSignedDocUrl,
    pdfUrl,
    needsPdfBackfill,
    ack.signedDocUrl,
    ack.logUrl,
  ]);

  useEffect(() => {
    if (!isOpen || !needsPdfBackfill || !message?._id || !ackUserId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await actions.messages.ensureAcknowledgmentReceipt(message._id, ackUserId);
      } catch {
        if (!cancelled) {
          setPdf(getPdf(message) || null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, needsPdfBackfill, message?._id, ackUserId, actions.messages, message]);

  if (!isOpen) return null;

  let downloadSrc;
  if (message?.digitalSignature) {
    downloadSrc = pdfUrl;
  } else if (effectiveLog && ack.logUrl) {
    downloadSrc = ack.logUrl;
  } else if (effectiveLog && !ack.logUrl) {
    downloadSrc = null;
  } else {
    downloadSrc = ack.signedDocUrl || pdf;
  }
  const attachmentName = getPdfName(message) || 'document.pdf';
  const downloadSrcIsAckStamped =
    !message?.digitalSignature && Boolean(ack.signedDocUrl) && downloadSrc === ack.signedDocUrl;
  const downloadName =
    effectiveLog && ack.logUrl
      ? `acknowledgment-receipt-${attachmentName.replace(/\.pdf$/i, '') || 'document'}.pdf`
      : downloadSrcIsAckStamped
        ? `acknowledged-${attachmentName}`
        : attachmentName;

  const showReceiptUnavailable =
    !message?.digitalSignature && effectiveLog && !ack.logUrl && acknowledge;

  const showDocumentCertificateToggle =
    acknowledge && (ack.logUrl || (!message?.digitalSignature && ack.signedDocUrl));

  const modalTitle = message?.digitalSignature
    ? getDigitalSignatureCardTitle(message)
    : message.text || message.title;

  const renderPdfArea = () => {
    if (showReceiptUnavailable) {
      return (
        <Typography
          color="textSecondary"
          sx={{
            p: '24px 16px',
            textAlign: 'center',
            maxWidth: 480,
          }}
        >
          {I18n.t('AcknowledgementReadModal.Receipt not available')}
        </Typography>
      );
    }
    if (pdf) {
      return (
        <Box
          sx={{
            maxWidth: 860,
            width: '90vw',
            height: '70vh',
            maxHeight: 'calc(100vh - 250px)',
            position: 'relative',
            '&::before': {
              content: `''`,
              position: 'absolute',
              boxShadow: 'inset 0px -10px 10px -10px rgba(0,0,0,0.5)',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              pointerEvents: 'none',
            },
          }}
        >
          <Box
            component="iframe"
            title="pdf"
            src={pdf}
            sx={{
              zIndex: 'initial',
              border: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      );
    }
    return <Loading size={40} key="loading" />;
  };

  return (
    <Modal isOpen={isOpen} onClose={actions.acknowledgmentReadModal.close}>
      <Box
        sx={{
          position: 'relative',
          p: '20px 0 10px',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="h3"
          sx={{
            fontSize: '20px',
            fontWeight: 'bold',
            m: '0 0 15px',
            textAlign: 'center',
            width: '100%',
            wordBreak: 'break-all',
          }}
        >
          {modalTitle}
        </Box>
        <Information
          isMe={isMe}
          digitalSignature={message.digitalSignature}
          confirmedAt={acknowledge?.confirmedAt}
          viewingReceipt={!message?.digitalSignature && effectiveLog}
        />
        {showDocumentCertificateToggle ? (
          <ToggleButtonGroup
            exclusive
            value={effectiveLog ? 'certificate' : 'document'}
            onChange={(e, v) => {
              if (v == null) return;
              if (v === 'certificate' && !ack.logUrl) return;
              setViewLog(v === 'certificate');
            }}
            size="small"
            color="primary"
            sx={{
              mb: '12px',
              '& .MuiToggleButton-root': {
                minHeight: 26,
                p: '2px 10px',
                fontSize: '0.75rem',
                lineHeight: 1.2,
              },
            }}
          >
            <ToggleButton value="document">
              {I18n.t('AcknowledgementReadModal.Document')}
            </ToggleButton>
            <ToggleButton value="certificate" disabled={!ack.logUrl}>
              {I18n.t('AcknowledgementReadModal.Certificate')}
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        {renderPdfArea()}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            mt: '20px',
            height: 39,
          }}
        >
          {downloadSrc ? (
            <DownloadButton
              color={acknowledge ? 'primary' : ''}
              variant={acknowledge ? 'contained' : 'outlined'}
              sx={{
                height: 39,
                mr: '10px',
              }}
              src={downloadSrc}
              name={downloadName}
              mimeType={getMimeType(message) || 'application/pdf'}
            />
          ) : null}
          {isCompanyAdmin || (isTeamAdmin && isDirect) || isAdmin ? (
            <Button variant="outlined" onClick={openAnalyticsDrawer}>
              {' '}
              {I18n.t('Documents.Monitor')}
            </Button>
          ) : null}
          {!acknowledge && isMe ? (
            <IUnderstoodButton
              onClose={actions.acknowledgmentReadModal.close}
              messageId={message._id}
            />
          ) : null}
        </Box>
      </Box>
    </Modal>
  );
};

ReadModal.propTypes = {
  isCompanyAdmin: bool.isRequired,
  isTeamAdmin: bool.isRequired,
  isOpen: bool.isRequired,
  message: messageShape,
  channel: channelShape,
  actions: shape({
    acknowledgmentReadModal: object.isRequired,
    messages: shape({
      ensureAcknowledgmentReceipt: func.isRequired,
    }).isRequired,
  }).isRequired,
  employee: employeeShape,
  authEmployee: employeeShape,
  log: bool,
};

export default connector(ReadModal);
